import { useContext, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';
import { ListPageTemplate } from '../../../components/page-templates/list-page/ListPageTemplate';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import RangesListPageContent from './components/RangesListPageContent';

const RangesPage = () => {
  const { t } = useTranslation('range');
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermissionTo } = useContext(UserContext);
  const [currentFilterSearchTerm, setCurrentFilterSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const canView = hasPermissionTo(['RangeRead']);
  const canCreate = hasPermissionTo(['RangeWrite']);
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setCurrentFilterSearchTerm(searchParams.get('searchTerm') || '');
  }, [location.search]);

  useEffect(() => {
    const message = (location.state as { successMessage?: string } | null)?.successMessage;
    if (!message) return;

    setSuccessMessage(message);
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [location.pathname, location.search, location.state, navigate]);

  const handleFilterChange = (searchTerm = currentFilterSearchTerm) => {
    setCurrentFilterSearchTerm(searchTerm);

    const params = new URLSearchParams();
    if (searchTerm) params.set('searchTerm', searchTerm);
    navigate(`?${params.toString()}`, { replace: true });
  };

  if (!canView) return null;

  return (
    <>
      <RangesListPageContent
        onFilterChange={handleFilterChange}
        initialSearchTerm={currentFilterSearchTerm}
      >
        {({ filterBar, headlineContent, table, isInitialLoading }) => (
          <ListPageTemplate
            title={t('rangesPageTitle')}
            subtitle={t('rangesPageSubtitle')}
            loading={isInitialLoading}
            loadingLabel={t('loading')}
            actions={canCreate && (
              <Button
                variant="primary"
                size="lg"
                startIcon={<Plus />}
                onClick={() => navigate('/range/ranges/new')}
              >
                {t('createRange')}
              </Button>
            )}
            headlineContent={headlineContent}
            filterContent={filterBar}
            table={table}
          />
        )}
      </RangesListPageContent>
      <MessagesSnackbar
        open={Boolean(successMessage)}
        onClose={() => setSuccessMessage(null)}
        message={successMessage ?? ''}
        severity="success"
        duration={4000}
      />
    </>
  );
};

export default RangesPage;
